/**
 * Permission codes for the roles & permissions system.
 * These codes must match the backend OrganizationPermissions constants.
 *
 * @see docs/features/companies/menu/USER_STORIES.md
 *
 * @userstory US-MENU-02, US-MENU-03, US-TEAM-03, US-TEAM-08
 */
export const PermissionCodes = {
	TALENTS: 'talents',
	PROJECTS: 'projects',
	QUOTES: 'quotes',
	FINANCE: 'finance',
	// LEGAL: 'legal',
} as const;

export type PermissionCode = (typeof PermissionCodes)[keyof typeof PermissionCodes];

/**
 * Organization roles
 */
export type OrganizationRole = 'Admin' | 'Member';

/**
 * Permission metadata for UI display
 */
export interface PermissionMeta {
	label: string;
	labelKey: string;
	description: string;
	descriptionKey: string;
	icon: string;
}

/**
 * Permission metadata lookup
 */
export const PermissionMetadata: Record<PermissionCode, PermissionMeta> = {
	[PermissionCodes.TALENTS]: {
		label: 'Talents',
		labelKey: 'permissions.talents',
		description: 'Search, AI Search, Messages, Saved Searches',
		descriptionKey: 'permissions.talents.description',
		icon: '👥',
	},
	[PermissionCodes.PROJECTS]: {
		label: 'Projects',
		labelKey: 'permissions.projects',
		description: 'Projects, Milestones, Timesheets',
		descriptionKey: 'permissions.projects.description',
		icon: '📁',
	},
	[PermissionCodes.QUOTES]: {
		label: 'Quotes',
		labelKey: 'permissions.quotes',
		description: 'Manage quotes, quote requests, and contract types',
		descriptionKey: 'permissions.quotes.description',
		icon: '📝',
	},
	[PermissionCodes.FINANCE]: {
		label: 'Finance',
		labelKey: 'permissions.finance',
		description: 'Invoices and financial reports',
		descriptionKey: 'permissions.finance.description',
		icon: '💰',
	},
	// [PermissionCodes.LEGAL]: {
	// 	label: 'Legal',
	// 	labelKey: 'permissions.legal',
	// 	description: 'Master Agreement, contract templates, legal audit',
	// 	descriptionKey: 'permissions.legal.description',
	// 	icon: '⚖️',
	// },
};

/**
 * Get all permission codes as an array
 */
export const getAllPermissionCodes = (): PermissionCode[] => {
	return Object.values(PermissionCodes);
};

/**
 * Check if a string is a valid permission code
 */
export const isValidPermissionCode = (code: string): code is PermissionCode => {
	return Object.values(PermissionCodes).includes(code as PermissionCode);
};
